import { useEffect, useState } from "react";
import { BarChart3, ChevronDown, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const rs = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

async function downloadCsv(path, name) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: name });
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch { toast.error("Could not download CSV"); }
}

export default function SalesReport({ refreshKey }) {
  const [open, setOpen] = useState(true);
  const [fy, setFy] = useState(0);
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/bills/report${fy ? `?fy=${fy}` : ""}`).then((r) => { setData(r.data); if (!fy) setFy(r.data.fy); }).catch(() => toast.error("Could not load sales report")); }, [fy, refreshKey]);
  const t = data?.totals;
  return (
    <section className="bg-white border border-ink/10 rounded-3xl overflow-hidden" data-testid="sales-report">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left" data-testid="sales-report-toggle" aria-expanded={open}>
        <span className="font-serif text-xl font-bold text-ink flex items-center gap-2"><BarChart3 size={18} className="text-terra" /> Sales report {data && <span className="text-ink/40 text-base font-normal">· {data.label}</span>}</span>
        <span className="flex items-center gap-4 text-xs text-ink/60">
          {t && <span className="hidden sm:inline" data-testid="sales-report-headline">Billed {rs(t.billed)} · Received {rs(t.received)} · GST {rs(t.gst)}</span>}
          <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && data && (
        <div className="px-6 pb-6" data-testid="sales-report-body">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <label className="text-[11px] font-mono uppercase tracking-widest text-ink/60">Financial year</label>
            <select value={fy} onChange={(e) => setFy(Number(e.target.value))} className="bg-sand border border-ink/15 rounded-xl px-3 py-1.5 text-sm" data-testid="sales-report-fy">
              {data.years.map((y) => <option key={y} value={y}>FY {y}-{String(y + 1).slice(2)}</option>)}
            </select>
            <div className="flex-1" />
            <button type="button" onClick={() => downloadCsv(`/admin/bills/report.csv?fy=${fy}`, `random-rider-sales-FY${fy}.csv`)} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-ink/15 px-3.5 py-2 rounded-full hover:bg-ink hover:text-sand transition-colors" data-testid="sales-report-csv-summary"><FileSpreadsheet size={13} /> Monthly summary CSV</button>
            <button type="button" onClick={() => downloadCsv(`/admin/bills/gstr.csv?fy=${fy}`, `random-rider-gstr-FY${fy}.csv`)} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-terra/40 text-terra px-3.5 py-2 rounded-full hover:bg-terra hover:text-sand transition-colors" data-testid="sales-report-csv-gstr"><FileSpreadsheet size={13} /> GSTR-1 CSV</button>
            <button type="button" onClick={() => downloadCsv(`/admin/bills/export.csv?fy=${fy}`, `random-rider-bills-FY${fy}.csv`)} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-ink text-sand px-3.5 py-2 rounded-full hover:bg-terra transition-colors" data-testid="sales-report-csv-bills"><Download size={13} /> All bills CSV</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {[["Billed", t.billed], ["Received", t.received], ["Outstanding", t.outstanding], ["GST collected", t.gst], ["Bills", t.count]].map(([k, v]) => (
              <div key={k} className="bg-sand rounded-2xl px-4 py-3" data-testid={`sales-total-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="text-[10px] font-mono uppercase tracking-widest text-ink/50">{k}</p>
                <p className="font-serif text-lg font-bold text-ink mt-0.5">{k === "Bills" ? v : rs(v)}</p>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-sm min-w-[560px]" data-testid="sales-report-table">
              <thead><tr className="text-[10px] font-mono uppercase tracking-widest text-ink/50 border-b border-ink/10">
                <th className="text-left py-2 font-medium">Month</th><th className="text-right py-2 font-medium">Bills</th><th className="text-right py-2 font-medium">Billed</th><th className="text-right py-2 font-medium">Received</th><th className="text-right py-2 font-medium">Outstanding</th><th className="text-right py-2 font-medium">GST</th>
              </tr></thead>
              <tbody>
                {data.months.map((m) => (
                  <tr key={m.month} className={`border-b border-ink/5 ${m.count ? "text-ink" : "text-ink/35"}`} data-testid={`sales-month-${m.month.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="py-2 font-medium">{m.month}</td><td className="text-right py-2">{m.count}</td><td className="text-right py-2">{rs(m.billed)}</td><td className="text-right py-2">{rs(m.received)}</td><td className="text-right py-2">{rs(m.outstanding)}</td><td className="text-right py-2">{rs(m.gst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
